import { useState, useRef, useEffect } from 'react';

import VolumeSlider from './VolumeSlider';
import PlayerTimeline from './PlayerTimeline';

import Icon from 'components/icon/Icon';
import Button from 'components/button/Button';

import { IAudioPlayer } from 'source/NFR.types';

const timeString = (val: number): string => {
  const toDoubleDigits = (n: string) => (n.length < 2 ? `0${n}` : n);
  const seconds = Math.floor(val);

  const sec = (seconds % 60).toString();
  const min = Math.floor(seconds / 60).toString();

  return `${toDoubleDigits(min)}:${toDoubleDigits(sec)}`;
};

const AudioPlayer: React.FC<IAudioPlayer> = ({ url, labels, altText }) => {
  const [volume, setVolume] = useState<number>(0.5);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [secondsPlayed, setSecondsPlayed] = useState<number>(0);
  const [secondsTotal, setSecondsTotal] = useState<number>(0);
  const [showVolumeSlider, setShowVolumeSlider] = useState<boolean>(false);

  const audioPlayer = useRef<HTMLAudioElement>(null);
  const volumeBtn = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!audioPlayer.current) return;

    if (isPlaying) {
      void audioPlayer.current.play();
    } else {
      audioPlayer.current.pause();
    }
  }, [isPlaying]);

  useEffect(() => {
    if (!audioPlayer.current) return;
    audioPlayer.current.volume = volume;
  }, [volume]);

  useEffect(() => {
    volumeBtn.current?.addEventListener('touchstart', function (e) {
      e.preventDefault(); // Prevents scrolling
      setShowVolumeSlider(true);
    });
  }, []);

  if (!url) return null;

  const setTime = (value: number) => {
    const newTime = value * secondsTotal;
    if (!audioPlayer.current) return;

    audioPlayer.current.currentTime = newTime;
  };

  return (
    <div className="audio-player">
      <div className="audio-player--row">
        <Button
          className="audio-player--button"
          onClick={() => setIsPlaying(prevState => !prevState)}
          icon={isPlaying ? 'stop' : 'play'}
          iconFill
          aria-label={isPlaying ? labels.pause : labels.play}
        />
        <span className="audio-player--duration">
          {timeString(secondsPlayed)}/{timeString(secondsTotal)}
        </span>

        <PlayerTimeline
          className="audio-player--timeline-desktop"
          progress={secondsPlayed / secondsTotal}
          setTime={setTime}
        />
        <div
          className="audio-player--volume audio-player--button"
          onMouseEnter={() => setShowVolumeSlider(true)}
          onTouchEnd={() => setShowVolumeSlider(false)}
          onFocus={() => setShowVolumeSlider(true)}
          onBlur={() => setShowVolumeSlider(false)}
          ref={volumeBtn}
          tabIndex={0}
        >
          {showVolumeSlider && (
            <VolumeSlider volume={volume} setVolume={setVolume} close={() => setShowVolumeSlider(false)} />
          )}
          <Icon name="volume" fill />
        </div>
        <a className="audio-player--button" aria-label={labels.download} href={url} download>
          <Icon name="download" fill />
        </a>
        <audio
          src={url}
          ref={audioPlayer}
          onTimeUpdate={e => setSecondsPlayed(e.currentTarget.currentTime)}
          onCanPlay={e => setSecondsTotal(e.currentTarget.duration)}
        >
          {altText}
        </audio>
      </div>
      <PlayerTimeline
        className="audio-player--timeline-mobile"
        progress={secondsPlayed / secondsTotal}
        setTime={setTime}
      />
    </div>
  );
};

AudioPlayer.displayName = 'AudioPlayer';

export default AudioPlayer;
